import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import axios from 'axios';
import { apiUrl } from '../../apiBase';

// NoticeIA has its own account pool, separate from Lumna. This context owns that
// session: it talks only to /api/notice/auth/*, keeps its own notice_* cookies,
// and refreshes 401s on /api/notice/ requests via the NoticeIA refresh route (the
// shared AuthContext interceptor deliberately skips /api/notice/).
export interface NoticeUser {
  id: number;
  email: string;
  name: string | null;
  picture: string | null;
  is_admin: boolean;
  // NOTICE_OWNER_EMAIL's account (see notice_auth._is_owner).
  is_owner?: boolean;
}

interface NoticeAuthType {
  user: NoticeUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (credential: string) => Promise<void>;
  logout: () => Promise<void>;
}

const NoticeAuthContext = createContext<NoticeAuthType | undefined>(undefined);

// True for NoticeIA data requests that should trigger a NoticeIA refresh (not the
// auth routes themselves, which must fail outright rather than loop).
function isNoticeApiUrl(url?: string): boolean {
  return !!url && url.includes('/api/notice/') && !url.includes('/api/notice/auth/');
}

// Single-flight refresh. The notice refresh token is single-use and rotated on the
// server (consume deletes the row, a new one is issued), so two refreshes racing on
// the same cookie means the second presents an already-consumed token and 401s —
// which logged the user out. Switching to a tab that fans out several /api/notice/
// requests at once (e.g. the catalog's three parallel GETs) reliably triggered that
// race once the 15-minute access token had expired.
//
// Coalescing every concurrent 401 onto ONE in-flight refresh means the token rotates
// exactly once per burst; the rest await the same promise and then retry. Resolves
// true on success, false on failure — never rejects, so callers branch on the value.
let refreshInFlight: Promise<boolean> | null = null;

function refreshOnce(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = fetch(apiUrl('/api/notice/auth/refresh'), {
      method: 'POST',
      credentials: 'include',
    })
      .then((r) => r.ok)
      .catch(() => false)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

export function NoticeAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<NoticeUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Refresh the NoticeIA session on a 401 from a NoticeIA data request, then retry
  // once. Scoped to /api/notice/ so it never fights the shared Lumna interceptor.
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (r) => r,
      async (error) => {
        const original = error.config;
        if (
          error.response?.status !== 401 ||
          !isNoticeApiUrl(original?.url) ||
          original?._noticeRetry
        ) {
          return Promise.reject(error);
        }
        original._noticeRetry = true;
        if (await refreshOnce()) {
          return axios(original);
        }
        setUser(null);
        return Promise.reject(error);
      },
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  useEffect(() => {
    void checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      let res = await fetch(apiUrl('/api/notice/auth/me'), { credentials: 'include' });
      let data = await res.json();
      if (!data.user && (await refreshOnce())) {
        res = await fetch(apiUrl('/api/notice/auth/me'), { credentials: 'include' });
        data = await res.json();
      }
      setUser(data.user ?? null);
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (credential: string) => {
    const res = await fetch(apiUrl('/api/notice/auth/google'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ credential }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Login failed');
    }
    const data = await res.json();
    setUser(data.user ?? null);
  };

  const logout = async () => {
    try {
      await fetch(apiUrl('/api/notice/auth/logout'), { method: 'POST', credentials: 'include' });
    } catch {
      // ignore
    }
    setUser(null);
  };

  return (
    <NoticeAuthContext.Provider
      value={{ user, isLoading, isAuthenticated: !!user, login, logout }}
    >
      {children}
    </NoticeAuthContext.Provider>
  );
}

export function useNoticeAuth(): NoticeAuthType {
  const ctx = useContext(NoticeAuthContext);
  if (!ctx) throw new Error('useNoticeAuth must be used within a NoticeAuthProvider');
  return ctx;
}
